// The platform's Dashboard and Customers pages (D-182): whether each company is getting value from Voxowa, and the
// first signs of one slipping, from counts alone. The Dashboard shows what needs a look today; Customers lists the
// companies by segment. Voxowa's own company is kept apart and never counted.
import { useState } from "react";
import { Link, NavLink, useParams } from "react-router";
import { useSession } from "../app/session";
import { Card, Empty, ErrorNote, Loading, Page, Stat, useLoad } from "../app/ui";
import {
  byAttention,
  change,
  checklistProgress,
  inSegment,
  integrationName,
  integrationsInUse,
  integrationState,
  type Segment,
  SEGMENTS,
  share,
  STATUS_LABEL,
  STATUS_TONE,
  statusCounts,
} from "../lib/companyHealth";
import { fmtNumber } from "../lib/format";
import { ago } from "../lib/teamHealth";
import type { CompanyHealth, PlatformOrg } from "../lib/types";
import { AddOnBadges, industryName, NewOrganization, PlatformShell, waiting } from "./Platform";

/** At risk, Watch, Onboarding, Healthy or Suspended, and a quiet spell or Voxowa's own beside it. */
export function StatusBadges({ c }: { c: CompanyHealth }) {
  return (
    <span className="row" style={{ gap: 4 }}>
      <span className={`badge ${STATUS_TONE[c.status]}`}>{STATUS_LABEL[c.status]}</span>
      {c.internal && <span className="badge cold">Voxowa's own</span>}
      {c.quiet && <span className="badge">Quiet until {shortDate(c.quiet_until!)}</span>}
    </span>
  );
}

/** "20 Oct". */
export function shortDate(day: string): string {
  return new Date(`${day}T00:00:00Z`).toLocaleDateString("en-IN", { day: "numeric", month: "short", timeZone: "UTC" });
}

export function PlatformDashboard() {
  const { api } = useSession();
  const overview = useLoad(() => api.platformHealthOverview(), [api]);
  const companies = overview.data?.companies ?? [];
  const counts = statusCounts(companies);
  const attention = inSegment(companies, "attention").sort(byAttention);
  const onboarding = inSegment(companies, "onboarding").sort((a, b) => a.days_live - b.days_live);
  const week = overview.data?.week;

  return (
    <PlatformShell>
      <Page title="Dashboard" sub="How the companies use Voxowa, from counts alone: never their customers or calls.">
        <ErrorNote error={overview.error} />
        {overview.loading && !overview.data && <Loading />}
        {overview.data && (
          <div className="stack">
            <div className="grid cols-4">
              <Stat label="At risk" value={counts.at_risk} alert={counts.at_risk > 0} href="#/customers/attention" />
              <Stat label="Watch" value={counts.watch} href="#/customers/attention" />
              <Stat
                label="Onboarding"
                value={counts.onboarding}
                hint="Their first 14 days"
                href="#/customers/onboarding"
              />
              <Stat label="Healthy" value={counts.healthy} href="#/customers/healthy" />
            </div>

            <Card
              title="Needs attention"
              actions={<Link className="button small" to="/customers/attention">All of them</Link>}
            >
              {attention.length === 0
                ? <Empty>Nothing needs attention: every company is calling and following up as usual.</Empty>
                : (
                  <div className="list">
                    {attention.map((c) => (
                      <div className="list-item" key={c.org_id}>
                        <div className="row between">
                          <Link to={`/org/${c.org_id}`}>
                            <strong>{c.name}</strong>
                          </Link>
                          <StatusBadges c={c} />
                        </div>
                        <ul className="reasons">
                          {c.reasons.map((r) => <li key={r}>{r}</li>)}
                        </ul>
                        {c.note && <div className="faint">Note: {c.note}</div>}
                      </div>
                    ))}
                  </div>
                )}
            </Card>

            <div className="grid cols-2">
              <Card title="Onboarding">
                {onboarding.length === 0 ? <Empty>No company in its first 14 days.</Empty> : (
                  <div className="list">
                    {onboarding.map((c) => {
                      const p = checklistProgress(c);
                      return (
                        <div className="list-item" key={c.org_id}>
                          <div className="row between">
                            <Link to={`/org/${c.org_id}`}>
                              <strong>{c.name}</strong>
                            </Link>
                            <span className="faint">Day {c.days_live + 1} of 14</span>
                          </div>
                          <div className="meter" aria-label={`${p.done} of ${p.total} done`}>
                            <div style={{ width: `${(100 * p.done) / p.total}%` }} />
                          </div>
                          <div className="faint">
                            {p.done} of {p.total} done{p.next ? ` · next: ${p.next.toLowerCase()}` : ""}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card>

              <Card title="This week">
                {week && (
                  <div className="list">
                    <div className="list-item row between">
                      <span>Calls</span>
                      <span>
                        <strong>{fmtNumber(week.calls_7)}</strong>{" "}
                        <span className="faint">
                          {change(week.calls_7, week.calls_prev_7) ?? "–"} on last week ({fmtNumber(week.calls_prev_7)})
                        </span>
                      </span>
                    </div>
                    <div className="list-item row between">
                      <span>People calling</span>
                      <strong>{fmtNumber(week.people_active_7)}</strong>
                    </div>
                    <div className="list-item row between">
                      <span>Follow-ups done</span>
                      <span>
                        <strong>{fmtNumber(week.done_7)}</strong>{" "}
                        <span className="faint">{share(week.on_time_7, week.done_7)} on time</span>
                      </span>
                    </div>
                    <div className="list-item faint">Voxowa's own company is left out of these.</div>
                  </div>
                )}
              </Card>
            </div>
          </div>
        )}
      </Page>
    </PlatformShell>
  );
}

export function PlatformCustomers() {
  const { api } = useSession();
  const params = useParams();
  const segment = (SEGMENTS.some((s) => s.key === params.segment) ? params.segment : "attention") as Segment | "all";
  const overview = useLoad(() => api.platformHealthOverview(), [api]);
  const orgs = useLoad(() => api.platformOrgs(), [api]);
  const [creating, setCreating] = useState(false);
  const companies = overview.data?.companies ?? [];
  const shown = inSegment(companies, segment).sort(byAttention);
  const byId = new Map((orgs.data ?? []).map((o) => [o.id, o]));
  const reload = async () => {
    await Promise.all([overview.reload(), orgs.reload()]);
  };

  return (
    <PlatformShell>
      <Page
        title="Customers"
        sub="Each company's use of Voxowa this week against its usual week."
        actions={<button className="primary" onClick={() => setCreating(true)}>New organization</button>}
      >
        <nav className="page-tabs" aria-label="Segments">
          {SEGMENTS.map((s) => (
            <NavLink key={s.key} to={`/customers/${s.key}`} className={s.key === segment ? "active" : ""}>
              {s.label} <span className="faint">{inSegment(companies, s.key).length}</span>
            </NavLink>
          ))}
        </nav>
        <ErrorNote error={overview.error ?? orgs.error} />
        {overview.loading && !overview.data && <Loading />}
        {overview.data && (
          <Card flush>
            {shown.length === 0
              ? (
                <div style={{ padding: 16 }}>
                  <Empty>No company here.</Empty>
                </div>
              )
              : <CompanyTable companies={shown} orgs={byId} />}
          </Card>
        )}
      </Page>
      {creating && <NewOrganization onClose={() => setCreating(false)} onDone={reload} />}
    </PlatformShell>
  );
}

function CompanyTable({ companies, orgs }: { companies: CompanyHealth[]; orgs: Map<string, PlatformOrg> }) {
  const now = new Date();
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Company</th>
            <th>Status</th>
            <th className="num">People</th>
            <th className="num">Calls this week</th>
            <th className="num">Follow-ups</th>
            <th className="num">Phones synced</th>
            <th>Owner or manager</th>
          </tr>
        </thead>
        <tbody>
          {companies.map((c) => {
            const org = orgs.get(c.org_id);
            const m = c.metrics;
            return (
              <tr key={c.org_id}>
                <td>
                  <div className="primary">
                    <Link to={`/org/${c.org_id}`}>{c.name}</Link>
                  </div>
                  <div className="row" style={{ gap: 4 }}>
                    <AddOnBadges addOns={c.features} memberLimit={c.member_limit} />
                    {org?.reviewed_at === null && (
                      <span className="badge warm">Under review · {waiting(org.held_recordings)}</span>
                    )}
                  </div>
                  <div className="faint">{industryName(c.vertical)} · day {fmtNumber(c.days_live + 1)}</div>
                  {integrationsInUse(c).length > 0 && (
                    <div className="faint">
                      {integrationsInUse(c).map((i, n) => {
                        const state = integrationState(i);
                        return (
                          <span key={i.key}>
                            {n > 0 && ", "}
                            {integrationName(i.key)}
                            {state.label !== "Connected" && (
                              <span className={state.tone === "danger" ? "danger-text" : ""}>
                                {" "}({state.label.toLowerCase()})
                              </span>
                            )}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </td>
                <td style={{ minWidth: 240 }}>
                  <StatusBadges c={c} />
                  {c.reasons[0] && <div className="faint">{c.reasons[0]}</div>}
                  {c.reasons.length > 1 && <div className="faint">and {c.reasons.length - 1} more</div>}
                  {c.status === "onboarding" && (
                    <div className="faint">
                      {checklistProgress(c).done} of {checklistProgress(c).total} done
                    </div>
                  )}
                </td>
                <td className="num">
                  {m.members} <span className="faint">({m.callers} callers)</span>
                </td>
                <td className="num">
                  {fmtNumber(m.calls_7)}
                  {m.calls_usual !== null && c.status !== "suspended" && (
                    <div className="faint">
                      {change(m.calls_7, m.calls_usual)} · usually {fmtNumber(m.calls_usual)}
                    </div>
                  )}
                </td>
                <td className="num">
                  {fmtNumber(m.done_7)} done
                  <div className="faint">
                    {share(m.on_time_7, m.done_7)} on time · {fmtNumber(m.overdue)} overdue
                  </div>
                </td>
                <td className="num">
                  {m.phones_synced_24h} / {m.phones}
                </td>
                <td className="nowrap">
                  {m.manager_seen_at ? ago(m.manager_seen_at, now) : <span className="faint">Not seen</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
